/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from 'src/app/service/config.service';
import { RoutingService } from 'src/app/service/routing.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { ScoreEventsService } from 'src/app/shared/services/data-api/score-events.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { ParamsBuilder } from 'src/app/shared/utils/params-builder';
import {
  TopEventsRequestParams,
  BullBearTopEventsRequestParams,
  TopEventsResponse,
} from 'src/app/shared/models/score-events.interface';
import { QueryParams } from 'src/app/shared/models/query-params.interface';

export interface ChartmapTopEventsOptions {
  /** Legacy `iparams.requestScore` (flag score type, 'esg', 'credit_risk', alert score, ...). */
  requestScore?: string;
  /** Legacy `iparams.score_type` override (ESG sites pass 'e_standard' etc.). */
  scoreType?: string;
  /** Legacy `with_all_events` — true → n_events=-1; false → param omitted (ESG e/s/g sites). */
  withAllEvents?: boolean;
  /** Legacy `top_events_sign` — only 'neg' is ever forwarded. */
  topEventsSign?: 'neg' | null;
  /** Legacy `time` override (may be null → falls back to 7 days, like myParams). */
  time?: string | number | null;
  /**
   * Which ranking to fetch. Only the active one is fetched (lazy) — the mosaic
   * defaults to `relative_importance` (the default sort) and re-fetches with
   * `absolute_count` when the user switches to the Count view.
   */
  ranking?: 'absolute_count' | 'relative_importance';
}

// URL-filter params that legacy myParams() forwarded to score_ts_top_events.
const FILTER_PARAM_KEYS = [
  'exclude_document_category_filter',
  'source_continents',
  'source_countries',
  'factuality',
  'temporality',
  'source_ranks',
  'source_panels',
] as const;

const SCORE_PERIOD = 30;

interface ResolvedScoreParams {
  scoreType?: string;
  eventWeights?: Record<string, number>;
  withBlackSwan?: boolean;
  disableIndustryMateriality?: boolean;
  riskSensitivity?: number;
  timeHorizon?: string;
}

/**
 * Bug-for-bug replacement for the chartmap usages of
 * `yukkApi.scoresTimeSeries('score', { with_top_events: true, ... }, 'chart')`:
 * same params (period forced to 30, n_events only with `with_all_events`,
 * URL filters forwarded, lang/feed omitted when absent), same context dispatch
 * (market/folio/query/search). Callers that pass an explicit `ranking` (the
 * Score Events mosaic) fetch only that one lazily; callers that don't (Flag
 * Events) keep the legacy 2-ranking forkJoin + importance merge.
 */
@Injectable({ providedIn: 'root' })
export class ChartmapTopEventsService {
  constructor(
    private config: ConfigService,
    private routing: RoutingService,
    private featureFlagsService: FeatureFlagsService,
    private portfolioApi: PortfolioApiService,
    private queryApi: QueryApiService,
    private scoreEventsService: ScoreEventsService,
    private scoreUtils: ScoreUtilsService,
  ) {}

  fetchTopEvents(
    qparam: QueryParams,
    opts: ChartmapTopEventsOptions = {},
  ): Observable<any> {
    const defaultScore = this.featureFlagsService.scorelabSettings.defaultScore;
    const isBullBear =
      opts.requestScore === 'bb' ||
      (!opts.requestScore && defaultScore === 'bb');

    const base = this.buildBaseParams(qparam, opts);
    const score = this.resolveScoreParams(qparam, opts);
    const sign = this.resolveTopEventsSign(opts, isBullBear, defaultScore);

    // When the caller names a `ranking` (the Score Events mosaic, which fetches
    // lazily per the selected sort), fetch ONLY that ranking. The API's
    // `contrib_amount` is whatever the requested ranking measures, so for the
    // importance ranking we mirror it into `contrib_amount_importance` — that
    // makes the template's filter/sort/display work unchanged for either ranking
    // without pre-fetching both.
    if (opts.ranking) {
      const ranking = opts.ranking;
      return this.dispatch(
        qparam,
        isBullBear,
        this.buildRequestParams(base, score, sign, opts, ranking, isBullBear),
      ).pipe(
        map((resp) => {
          const topEvents = JSON.parse(JSON.stringify(resp));
          if (ranking === 'relative_importance') {
            topEvents.top_events?.forEach((event: any, index: number) => {
              topEvents.top_events[index].contrib_amount_importance =
                event.contrib_amount;
            });
          }
          return topEvents;
        }),
      );
    }

    // Legacy default (Flag Events + any caller that doesn't opt into lazy):
    // fetch both rankings and merge importance into the count response.
    const count$ = this.dispatch(
      qparam,
      isBullBear,
      this.buildRequestParams(
        base,
        score,
        sign,
        opts,
        'absolute_count',
        isBullBear,
      ),
    );
    const importance$ = this.dispatch(
      qparam,
      isBullBear,
      this.buildRequestParams(
        base,
        score,
        sign,
        opts,
        'relative_importance',
        isBullBear,
      ),
    );

    return forkJoin({ count: count$, importance: importance$ }).pipe(
      map((resp) => {
        const respCopy = JSON.parse(JSON.stringify(resp));
        const topEvents = respCopy.count;
        topEvents.top_events.forEach((event: any, index: number) => {
          const importanceEventInstance =
            respCopy.importance.top_events?.filter(
              (e: any) => e.id === event.id,
            )[0];
          topEvents.top_events[index].contrib_amount_importance =
            importanceEventInstance
              ? importanceEventInstance.contrib_amount
              : null;
        });
        return topEvents;
      }),
    );
  }

  // ---------------------------------------------------------------------
  // Params
  // ---------------------------------------------------------------------

  private buildBaseParams(qparam: QueryParams, opts: ChartmapTopEventsOptions) {
    // Legacy myParams() ignores `newsday` — strip it so ParamsBuilder cannot
    // narrow the range; `time: null` overrides fall back to the 7-day default.
    const iparams = { ...qparam };
    delete iparams.newsday;
    if (opts.time !== undefined) {
      iparams.time = opts.time;
    }
    if (!iparams.time) {
      delete iparams.time;
    }
    return ParamsBuilder.build(
      iparams,
      ['period', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS],
      this.config.appConfig.routing.apiproj,
    );
  }

  private buildRequestParams(
    base: Record<string, any>,
    score: ResolvedScoreParams,
    sign: 'neg' | undefined,
    opts: ChartmapTopEventsOptions,
    ranking: 'absolute_count' | 'relative_importance',
    isBullBear: boolean,
  ): TopEventsRequestParams & BullBearTopEventsRequestParams {
    const extraFilters: Record<string, unknown> = {};
    FILTER_PARAM_KEYS.forEach((key) => {
      if (base[key] !== undefined) {
        extraFilters[key] = base[key];
      }
    });

    // Legacy bull-bear routing with a score_type fall-through (defaultScore
    // 'bb' + explicit score_type, e.g. alert-ESG e/s/g sites) still sends the
    // score params on the bull_bear endpoint — the typed BB request has no
    // score fields, so forward them verbatim.
    if (isBullBear) {
      if (score.scoreType) {
        extraFilters['score_type'] = score.scoreType;
      }
      if (score.disableIndustryMateriality) {
        extraFilters['disable_industry_materiality'] = true;
      }
    }

    return {
      dateFrom: base['from'],
      dateTo: base['to'],
      period: SCORE_PERIOD,
      ranking,
      nEvents: opts.withAllEvents ? -1 : undefined,
      lang: base['lang'] ? String(base['lang']).split(',') : null,
      feedTypes: base['feed_types']
        ? String(base['feed_types']).split(',')
        : null,
      extraFilters,
      scoreType: score.scoreType,
      eventWeights: score.eventWeights,
      withBlackSwan: score.withBlackSwan,
      disableIndustryMateriality: score.disableIndustryMateriality,
      riskSensitivity: score.riskSensitivity,
      timeHorizon: score.timeHorizon,
      topEventsSign: sign,
    };
  }

  /**
   * Replicates the legacy score-param chain:
   * requestScore → iparams.score_type (≠ credit_risk/bb) → defaultScore.
   */
  private resolveScoreParams(
    qparam: QueryParams,
    opts: ChartmapTopEventsOptions,
  ): ResolvedScoreParams {
    const requestScore = opts.requestScore;
    const scoreTypeParam = opts.scoreType ?? qparam.score_type;

    if (requestScore) {
      return this.paramsForScoreKey(requestScore);
    }
    if (
      scoreTypeParam &&
      scoreTypeParam !== 'credit_risk' &&
      scoreTypeParam !== 'bb'
    ) {
      const params: ResolvedScoreParams = { scoreType: scoreTypeParam };
      if (
        ['esg', 'e_standard', 's_standard', 'g_standard'].includes(
          scoreTypeParam,
        )
      ) {
        params.disableIndustryMateriality = true;
      }
      // NOTE: no time_horizon here even when routed to bull_bear (legacy quirk).
      return params;
    }
    return this.paramsForScoreKey(
      this.featureFlagsService.scorelabSettings.defaultScore,
    );
  }

  private paramsForScoreKey(scoreKey: string): ResolvedScoreParams {
    switch (scoreKey) {
      case 'general_risk':
      case 'esg_risk':
      case 'immediate_risk':
        return { scoreType: scoreKey, withBlackSwan: true };
      case 'credit_risk':
        return { scoreType: 'credit_watch', riskSensitivity: 0 };
      case 'esg':
        return { scoreType: 'esg', disableIndustryMateriality: true };
      case 'eb_sim':
      case 'eb_sim_risk':
        return { scoreType: scoreKey };
      case 'bb':
        return { timeHorizon: '1M' };
      default: {
        const custom = this.scoreUtils.getCustomScoreEventWeights(scoreKey);
        if (custom) {
          return custom.withBlackSwan
            ? { eventWeights: custom.eventWeights, withBlackSwan: true }
            : { eventWeights: custom.eventWeights };
        }
        // Unknown/weightless custom uid → legacy sends NO score params at all
        // (never `score_type=<uid>` — the API 422s on it).
        return {};
      }
    }
  }

  /** Legacy adds top_events_sign=neg only for credit_risk (standard) or any bb call. */
  private resolveTopEventsSign(
    opts: ChartmapTopEventsOptions,
    isBullBear: boolean,
    defaultScore: string,
  ): 'neg' | undefined {
    if (opts.topEventsSign !== 'neg') {
      return undefined;
    }
    if (isBullBear) {
      return 'neg';
    }
    const creditRisk =
      (!opts.requestScore && defaultScore === 'credit_risk') ||
      opts.requestScore === 'credit_risk';
    return creditRisk ? 'neg' : undefined;
  }

  // ---------------------------------------------------------------------
  // Context dispatch (legacy iRequest order)
  // ---------------------------------------------------------------------

  private dispatch(
    qparam: QueryParams,
    isBullBear: boolean,
    params: TopEventsRequestParams & BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse | undefined> {
    if (this.routing.isMarket() || qparam.portfolioId) {
      if (qparam.portfolio) {
        if (this.portfolioApi.portfolios) {
          return this.portfolioApi.portfolios
            .filter((portfolio: any) => portfolio.uid === qparam.portfolioId)
            .map((item: any) =>
              this.portfolioRequest(isBullBear, item.content, params).pipe(
                catchError(() => of(undefined)),
              ),
            )[0];
        }
        return of({} as TopEventsResponse);
      }
      if (this.portfolioApi.activePortfolio?.marketPortfolio) {
        return this.portfolioRequest(
          isBullBear,
          this.portfolioApi.activePortfolio.content,
          params,
        ).pipe(catchError(() => of(undefined)));
      }
      return this.entityRequest(isBullBear, qparam, params);
    }

    if (this.routing.isFolio() && !qparam.portfolioId) {
      return this.portfolioRequest(
        isBullBear,
        this.portfolioApi.activePortfolio
          ? this.portfolioApi.activePortfolio.content
          : [],
        params,
      );
    }

    if (this.routing.isQuery() || this.routing.isSearch()) {
      if (this.routing.isSearch()) {
        // isSearch is the ONLY context where the raw query payload is allowed.
        return this.queryRequest(isBullBear, this.queryApi.query.query, params);
      }
      if (this.queryApi.query && qparam.groupId !== qparam.id) {
        return this.queryApi
          .resolveQuery(this.queryApi.query.uid)
          .pipe(
            switchMap((response: any) =>
              this.queryRequest(isBullBear, response.query, params),
            ),
          );
      }
      return this.queryApi.resolveQueryGroup(this.queryApi.query.uid).pipe(
        switchMap((response: any) => {
          const payload = {
            operator: 'or',
            filters: [],
            groups: response.content.map((item: any) => item.query),
          };
          return this.queryRequest(isBullBear, payload, params);
        }),
      );
    }

    return this.entityRequest(isBullBear, qparam, params);
  }

  private entityRequest(
    isBullBear: boolean,
    qparam: QueryParams,
    params: TopEventsRequestParams & BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    return isBullBear
      ? this.scoreEventsService.getBullBearTopEvents(
          qparam.id,
          qparam.type,
          params,
        )
      : this.scoreEventsService.getTopEvents(qparam.id, qparam.type, params);
  }

  private portfolioRequest(
    isBullBear: boolean,
    content: unknown[],
    params: TopEventsRequestParams & BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    return isBullBear
      ? this.scoreEventsService.getPortfolioBullBearTopEvents(content, params)
      : this.scoreEventsService.getPortfolioTopEvents(content, params);
  }

  private queryRequest(
    isBullBear: boolean,
    queryPayload: unknown,
    params: TopEventsRequestParams & BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    // NOTE: the bull-bear search_query endpoint does not exist upstream (404);
    // legacy built the same URL and failed identically — parity preserved.
    return isBullBear
      ? this.scoreEventsService.getQueryBullBearTopEvents(queryPayload, params)
      : this.scoreEventsService.getQueryTopEvents(queryPayload, params);
  }
}
